/**
 * Клиент Type2X API.
 *
 * Сеть никогда не находится в критическом пути нажатия: все вызовы отсюда
 * выполняются между сессиями и не блокируют тренировку (решение AD-003,
 * ТЗ FR-SY-04).
 */

import { API_PREFIX, type ApiError } from '@type2x/shared-contracts';

/** Результат вызова: успех с данными либо ошибка API. */
export type ApiResult<T> =
  { readonly ok: true; readonly data: T } | { readonly ok: false; readonly error: ApiError };

/** Настройки клиента. */
export interface ApiClientOptions {
  /** Базовый адрес API. Пустая строка — тот же источник. */
  readonly baseUrl?: string;
  /** Реализация fetch. Подменяется в тестах. */
  readonly fetchImpl?: typeof fetch;
}

const NETWORK_ERROR: ApiError = {
  code: 'service_unavailable',
  message: 'Нет связи с сервером',
  requestId: '',
};

/** Клиент API. */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? '';
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /**
   * Выполняет запрос к API.
   *
   * Сетевой сбой возвращается как обычная ошибка результата, а не исключение:
   * вызывающий обязан обработать офлайн, а не падать.
   */
  async request<T>(method: string, path: string, body?: unknown): Promise<ApiResult<T>> {
    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${API_PREFIX}${path}`, {
        method,
        credentials: 'include',
        headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
        body: body === undefined ? null : JSON.stringify(body),
      });
    } catch {
      return { ok: false, error: NETWORK_ERROR };
    }

    if (!response.ok) {
      return { ok: false, error: await this.readError(response) };
    }

    return { ok: true, data: (await response.json()) as T };
  }

  private async readError(response: Response): Promise<ApiError> {
    try {
      const body = (await response.json()) as Partial<ApiError>;
      return {
        code: body.code ?? 'internal_error',
        message: body.message ?? 'Ошибка запроса',
        requestId: body.requestId ?? '',
        ...(body.retryAfterSeconds === undefined
          ? {}
          : { retryAfterSeconds: body.retryAfterSeconds }),
      };
    } catch {
      return { code: 'internal_error', message: 'Ошибка запроса', requestId: '' };
    }
  }

  /** POST-запрос. */
  post<T>(path: string, body?: unknown): Promise<ApiResult<T>> {
    return this.request<T>('POST', path, body);
  }

  /** GET-запрос. */
  get<T>(path: string): Promise<ApiResult<T>> {
    return this.request<T>('GET', path);
  }

  /** PUT-запрос. */
  put<T>(path: string, body: unknown): Promise<ApiResult<T>> {
    return this.request<T>('PUT', path, body);
  }

  /** PATCH-запрос. */
  patch<T>(path: string, body: unknown): Promise<ApiResult<T>> {
    return this.request<T>('PATCH', path, body);
  }

  /** DELETE-запрос. Тело допустимо: удаление аккаунта подтверждается паролем. */
  delete<T>(path: string, body?: unknown): Promise<ApiResult<T>> {
    return this.request<T>('DELETE', path, body);
  }
}
